import { useState } from "react";
import { AlertTriangle, CheckCircle2, Cloud, DatabaseBackup, FlaskConical, HardDrive, KeyRound, Mail, Timer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { formatDate, formatNumber } from "@/lib/format";
import { kb } from "@/components/settings/backup/util";

const Card = ({ icon: Icon, title, value, sub, tone = "slate", testid }) => (
  <div className="panel p-3" data-testid={testid}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500"><Icon className="h-3.5 w-3.5" />{title}</div>
    <div className={`mt-1 text-[14px] font-semibold ${tone === "ok" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : "text-slate-900"}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-0.5 break-words">{sub}</div>}
  </div>
);

export function StatusCards({ d, onDone }) {
  const [busy, setBusy] = useState(false);
  const last = d.last_success;
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/backup/run"); toast.success(`Yedek alındı ve doğrulandı: ${data.name}`); onDone(); }
    catch (e) { toast.error(apiError(e, "Yedek alınamadı")); onDone(); }
    setBusy(false);
  };
  const simulate = async () => {
    try { await api.post("/backup/simulate-failure"); toast.warning("Hata simülasyonu kaydedildi (BAŞARISIZ). Sonraki başarılı yedekte uyarı kalkar."); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-3">
      {d.alert.message && (
        <div className="flex items-center gap-2 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-[13px] text-rose-800" data-testid="backup-alert">
          <AlertTriangle className="h-4 w-4" />{d.alert.message}
        </div>
      )}
      <div className="panel p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {last ? <CheckCircle2 className="h-6 w-6 text-emerald-600" /> : <AlertTriangle className="h-6 w-6 text-amber-600" />}
          <div>
            <div className="font-semibold" data-testid="backup-last-success">{last ? `Son başarılı yedek: ${formatDate(last.started_at)}` : "Henüz başarılı yedek yok"}</div>
            {last && <div className="text-[12px] text-slate-500">{last.name} · {kb(last.size_bytes)} · {formatNumber(last.documents)} belge · {last.collection_count} koleksiyon · SHA-256 {last.sha256?.slice(0, 12)}… · {last.verified ? "doğrulandı" : "doğrulanmadı"}</div>}
          </div>
        </div>
        <div className="flex gap-2">
          {d.is_super && <Button variant="outline" className="text-rose-700 border-rose-300" onClick={simulate} data-testid="backup-simulate-failure-button"><FlaskConical className="h-4 w-4 mr-1" />Hata Simülasyonu</Button>}
          <Button onClick={run} disabled={busy} data-testid="backup-run-now-button"><DatabaseBackup className="h-4 w-4 mr-1" />{busy ? "Yedek alınıyor…" : "Şimdi Yedek Al"}</Button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <Card icon={Timer} title="Zamanlama" value={d.schedule.daily} sub={`${d.schedule.weekly} · ${d.schedule.monthly}`} testid="backup-card-schedule" />
        <Card icon={HardDrive} title="Yerel Depo" value={`${d.local.files} dosya · ${kb(d.local.total_bytes)}`} sub={d.local.label} testid="backup-card-local" />
        <Card icon={KeyRound} title="Şifreleme" value={d.encryption.enabled ? d.encryption.algorithm : "Kapalı"} tone={d.encryption.enabled ? "ok" : "warn"} sub={d.encryption.key_source} testid="backup-card-encryption" />
        <Card icon={Cloud} title="Harici Kopya (S3)" value={d.s3.label} tone={d.s3.configured ? "ok" : "warn"} sub={d.s3.configured ? `${d.s3.bucket} · ${d.s3.region} · ${d.s3.last_upload ? `Son yükleme ${formatDate(d.s3.last_upload.at)}: ${d.s3.last_upload.label}` : "Henüz yükleme yok"} · ${d.s3.retention}` : "AWS anahtarları .env'e eklenince otomatik devreye girer"} testid="backup-card-s3" />
        <Card icon={Mail} title="Haftalık E-posta" value={d.email.configured ? `Alıcı: ${d.email.to}` : "Gönderim kapalı"} tone={d.email.configured ? "ok" : "warn"} sub={d.email.last ? `Son rapor: ${formatDate(d.email.last.at)} · ${d.email.last.label}` : d.schedule.email} testid="backup-card-email" />
      </div>
    </div>
  );
}
